<template>
  <div v-if="isEditing">
    <div class="prompt-selector">
      <Multiselect label="Prompt chain" :value="selectedPrompt" :options="promptOptions" :get-label-callback="p => p.label" @input="onInput" />
    </div>
    <div v-if="currentActionIndex > 0" class="row">
      <Multiselect
        label="Prompt context"
        :value="selectedContext"
        :options="contextOptions"
        :multiple="true"
        @input="onContextInput"
      />
    </div>
    <template v-if="selectedPrompt">
      <div class="row">
        VARIABLES:

        <div v-for="(variable, index) in value.variables" :key="index" class="variable-row">
          <div class="variable-fields">
            <TextField :value="value.variables[index].name" label="Variable name" @input="onVariableNameInput(index, $event)" />
            <Multiselect
              :value="value.variables[index].value"
              label="Variable value"
              :options="fieldOptions"
              @input="onVariableValueInput(index, $event)"
            />
            <Checkbox
              class="bind-context"
              :value="value.variables[index].bindToContext === true"
              label="Bind to prompt context"
              @input="onBindToContextInput(index, $event)"
            />
          </div>
          <TextField
            v-if="value.variables[index].value === customValueOption"
            class="custom-value"
            multiline
            :value="value.variables[index].customValue"
            label="Custom value"
            @input="onCustomValueInput(index, $event)"
          />
          <div v-if="value.variables[index].value === documentOption" class="document-source">
            <TextField :value="value.variables[index].docCode" label="Doc code" @input="onDocCodeInput(index, $event)" />
            <Multiselect
              :value="value.variables[index].selector || 'latest'"
              label="Selector"
              :options="documentSelectorOptions"
              @input="onSelectorInput(index, $event)"
            />
          </div>
          <div v-if="isFamilyRelation(value.variables[index].value)" class="family-fields">
            <Multiselect
              label="Fields"
              :value="value.variables[index].familyFields || []"
              :options="portfolioFieldOptions"
              :multiple="true"
              :multiselect="true"
              placeholder="Select fields"
              @input="onFamilyFieldsInput(index, $event)"
            />
          </div>
        </div>
        <div>
          <Button @click="addVariable">Add Variable</Button>
        </div>
      </div>
    </template>
  </div>
  <div v-else>
    {{ message }} "{{ selectedPrompt ? selectedPrompt.label : '' }}" prompt
    <span v-if="contextSummary"> using context from {{ contextSummary }}</span>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Multiselect from '@/components/common/Multiselect.vue';
import TextField from '@/components/common/TextField.vue';
import Button from '@/components/common/Button.vue';
import Checkbox from '@/components/common/Checkbox.vue';

const CUSTOM_VALUE_OPTION = 'custom value';
const DOCUMENT_OPTION = 'document';
const PARENTS_OPTION = 'parents';
const CHILDREN_OPTION = 'children';
const DOCUMENT_SELECTOR_OPTIONS = ['oldest', 'latest', 'all'];
const FAMILY_RELATION_OPTIONS = [PARENTS_OPTION, CHILDREN_OPTION];

export default {
  name: 'PromptSelector',
  components: {
    Multiselect,
    TextField,
    Button,
    Checkbox
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        promptId: null,
        variables: []
      })
    },
    isEditing: {
      type: Boolean,
      default: true
    },
    message: {
      type: String,
      default: 'Index will be updated with result of'
    },
    currentActionIndex: {
      type: Number,
      default: 0,
      validator: value => value >= 0
    }
  },
  computed: {
    ...mapState({
      prompts: state => state.prompts.collection,
      filterSettings: state => state.pipelines.settings
    }),
    customValueOption() {
      return CUSTOM_VALUE_OPTION;
    },
    documentOption() {
      return DOCUMENT_OPTION;
    },
    documentSelectorOptions() {
      return DOCUMENT_SELECTOR_OPTIONS;
    },
    priorActionResults() {
      return Array.from({ length: this.currentActionIndex }, (value, index) => `{{action.${index + 1}}}`);
    },
    contextOptions() {
      return this.priorActionResults;
    },
    selectedContext() {
      const value = this.value?.contextFromAction;
      if (!value) {
        return [];
      }
      return Array.isArray(value) ? value : [value];
    },
    contextSummary() {
      const selected = this.selectedContext;
      return selected.length ? selected.join(', ') : '';
    },
    promptOptions() {
      return this.prompts.map(prompt => ({
        label: prompt.name,
        value: prompt.id
      }));
    },
    selectedPrompt() {
      return this.promptOptions.find(prompt => prompt.value === this.value?.promptId) || null;
    },
    portfolioFieldOptions() {
      if (!this.filterSettings) {
        return [];
      }
      const settings = this.filterSettings.find(setting => setting.database === 'applications');
      if (!settings) {
        return [];
      }
      return Object.keys(settings.fields);
    },
    fieldOptions() {
      if (!this.filterSettings) {
        return [];
      }
      const settings = this.filterSettings.find(setting => setting.database === 'applications');
      if (!settings) {
        return [];
      }
      const fieldEntries = Object.entries(settings.fields);
      const mappedFields = fieldEntries.map(e => `{{${e[0]}}}`);
      mappedFields.unshift(...this.priorActionResults);
      mappedFields.unshift('{{file}}');
      mappedFields.unshift(CHILDREN_OPTION);
      mappedFields.unshift(PARENTS_OPTION);
      mappedFields.unshift(DOCUMENT_OPTION);
      mappedFields.unshift(CUSTOM_VALUE_OPTION);
      return mappedFields;
    }
  },
  async created() {
    if (!this.prompts || this.prompts.length === 0) {
      await this.$store.dispatch('prompts/getCollection');
    }
  },
  methods: {
    isFamilyRelation(value) {
      return FAMILY_RELATION_OPTIONS.includes(value);
    },
    onContextInput(selected) {
      const refs = (selected || []).filter(Boolean);
      this.emitOptions({
        contextFromAction: refs.length ? refs : null
      });
    },
    emitOptions(changes) {
      this.$emit('input', {
        ...this.value,
        ...changes
      });
    },
    onInput(selected) {
      this.emitOptions({
        promptId: selected ? selected.value : null,
        variables: []
      });
    },
    addVariable() {
      this.emitOptions({
        promptId: this.value?.promptId,
        variables: [...this.value.variables, { name: '', value: '' }]
      });
    },
    onVariableNameInput(index, newName) {
      const variables = this.value.variables.map((variable, i) => (i === index ? { ...variable, name: newName } : variable));
      this.emitOptions({ promptId: this.value?.promptId, variables });
    },
    onVariableValueInput(index, newValue) {
      const variables = this.value.variables.map((variable, i) => {
        if (i !== index) {
          return variable;
        }
        const next = {
          ...variable,
          value: newValue,
          customValue: newValue === CUSTOM_VALUE_OPTION ? '' : null,
          docCode: null,
          selector: null,
          familyFields: null
        };
        if (newValue === DOCUMENT_OPTION) {
          next.docCode = variable.docCode || '';
          next.selector = variable.selector || 'latest';
        } else if (FAMILY_RELATION_OPTIONS.includes(newValue)) {
          next.familyFields = Array.isArray(variable.familyFields) ? variable.familyFields : [];
        }
        return next;
      });
      this.emitOptions({ promptId: this.value?.promptId, variables });
    },
    onCustomValueInput(index, newCustomValue) {
      const variables = this.value.variables.map((variable, i) => (i === index ? { ...variable, customValue: newCustomValue } : variable));
      this.emitOptions({ promptId: this.value?.promptId, variables });
    },
    onDocCodeInput(index, docCode) {
      const variables = this.value.variables.map((variable, i) => (i === index ? { ...variable, docCode } : variable));
      this.emitOptions({ promptId: this.value?.promptId, variables });
    },
    onSelectorInput(index, selector) {
      const variables = this.value.variables.map((variable, i) => (i === index ? { ...variable, selector } : variable));
      this.emitOptions({ promptId: this.value?.promptId, variables });
    },
    onFamilyFieldsInput(index, familyFields) {
      const variables = this.value.variables.map((variable, i) => (i === index ? { ...variable, familyFields: familyFields || [] } : variable));
      this.emitOptions({ promptId: this.value?.promptId, variables });
    },
    onBindToContextInput(index, bindToContext) {
      const variables = this.value.variables.map((variable, i) => (i === index ? { ...variable, bindToContext } : variable));
      this.emitOptions({ promptId: this.value?.promptId, variables });
    }
  }
};
</script>
<style lang="scss" scoped>
.prompt-selector {
  margin-bottom: 20px;
}

.variable-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 12px;
  }
}

.variable-fields {
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(160px, 1.2fr) auto;
  align-items: end;
  gap: 12px;
}

.bind-context {
  padding-bottom: 4px;
  white-space: nowrap;
}

.custom-value {
  max-width: 640px;
}

.document-source {
  display: grid;
  grid-template-columns: 160px 140px;
  gap: 12px;
  max-width: 320px;
}

.family-fields {
  max-width: 640px;
}
</style>
